import type { Delivery } from '../types/delivery';

export function useGroupedCartItems() {
    const { cart } = useCart();
    const cartItemsStore = useCartItemsStore();
    const { cartItemsWithProduct } = storeToRefs(cartItemsStore);

    const vendorStore = useCustomVendorStore();
    const { vendors: vendorList } = useCustomVendorStore();

    // Group cart items by vendorId and associate name surcharge link
    const groupedCartItems = computed(() => {
        const grouped: Record<
            string,
            {
                items: (typeof cartItemsWithProduct.value)[0][];
                name: string;
                fees: string;
                link: string;
                combinedTaxes: {
                    percentage: number;
                    taxes: number;
                    taxRate: number;
                };
            }
        > = {};
        cartItemsWithProduct.value
            .filter(item => item.cartItem.type === 'product')
            .forEach(item => {
                const vendorId = item.cartItem.payload.vendorId;
                if (!grouped[vendorId]) {
                    grouped[vendorId] = {};
                    grouped[vendorId].items = [];
                }
                grouped[vendorId]?.items.push(item);
            });

        const vendorIds = Object.keys(grouped);
        vendorStore.fetchVendorsByIds(vendorIds);

        vendorIds.forEach(vendorId => {
            if (vendorList.has(vendorId)) {
                grouped[vendorId].vendor = vendorList.get(vendorId);
                const totalTax = grouped[vendorId].items.reduce((vendorTaxAccumulator, item) => {
                    const taxes = item.product?.calculatedPrice?.calculatedTaxes || [];
                    const taxSum = taxes.reduce((taxesAccumulator, tax) => taxesAccumulator + tax.tax, 0);
                    return vendorTaxAccumulator + taxSum * item.cartItem.quantity;
                }, 0);

                grouped[vendorId].combinedTaxes = {
                    taxes: totalTax,
                    percentage: cart.value.price?.taxRules[0].percentage,
                    taxRate: cart.value.price?.taxRules[0].taxRate,
                };
            }
        });

        return grouped;
    });

    const otherCartItems = computed(() => {
        return cartItemsWithProduct.value.filter(item => item.cartItem.type !== 'product');
    });

    // Get total price for each vendor's products
    const vendorTotals = computed(() => {
        const vendors = cart.value.extensions?.['vendor-details']?.vendorsTotalPrice || [];
        const totals: Record<string, number> = {};
        vendors.forEach(vendor => {
            totals[vendor.id] = vendor.totalPrice;
        });
        return totals;
    });

    const getShippingMethodCost = (vendorId: string | null) => {
        if (!vendorId)
            return {
                totalPrice: 0,
                tax: 0,
                freeShippingThreshold: Infinity,
            };
        const deliveries = cart.value.deliveries || [];
        const vendor = vendorList.get(vendorId);
        const shippingMethodId = vendor?.shippingMethodId;
        const delivery = deliveries.find((delivery: Delivery) => delivery.shippingMethod.id === shippingMethodId);

        if (!delivery)
            return {
                totalPrice: 0,
                tax: 0,
                freeShippingThreshold: Infinity,
            };

        const prices = delivery.shippingMethod?.prices || [];
        const cartVendorTotal = vendorTotals.value[vendorId] || 0;

        const totalPrice = delivery.shippingCosts?.totalPrice;
        let selectedPriceTier = null;
        let freeShippingThreshold = Infinity;
        const shippingDeliveryTime = delivery.shippingMethod?.deliveryTime?.translated?.name;

        for (const price of prices) {
            const { quantityStart, quantityEnd, currencyPrice } = price;
            if (cartVendorTotal >= quantityStart && (quantityEnd === null || cartVendorTotal < quantityEnd)) {
                selectedPriceTier = currencyPrice?.[0];

                if (quantityEnd !== null) {
                    freeShippingThreshold = quantityEnd;
                }
                break;
            }
        }

        if (!selectedPriceTier) {
            return {
                totalPrice: 0,
                tax: 0,
                freeShippingThreshold,
                shippingDeliveryTime,
            };
        }

        if(totalPrice === 0) {
            freeShippingThreshold = Infinity;
        }

        const tax =
            delivery.shippingCosts?.calculatedTaxes.reduce(
                (taxAccumulator, tax) => taxAccumulator + (tax.tax || 0),
                0,
            ) ?? 0;

        return { totalPrice, tax, freeShippingThreshold, shippingDeliveryTime };
    };

    const amountTillFreeShipping = computed(() => {
        return (vendorId: string | null) => {
            if (!vendorId) return 0;
            const shippingCost = getShippingMethodCost(vendorId);
            const vendorCartTotal = vendorTotals.value[vendorId] || 0;

            return Math.max(0, shippingCost.freeShippingThreshold - vendorCartTotal);
        };
    });

    const fetchVendorList = async () => {
        const vendorIds = cart.value.lineItems
            .map(item => item.payload.vendorId);

        const uniqueVendorIds = [...new Set(vendorIds)];
        if (uniqueVendorIds.length > 0) {
            await vendorStore.fetchVendorsByIds(uniqueVendorIds);
        }
    };

    return {
        groupedCartItems,
        fetchVendorList,
        vendorTotals,
        otherCartItems,
        getShippingMethodCost,
        amountTillFreeShipping,
    };
}
